import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BRAND } from "../lib/site";

const EASE = [0.16, 1, 0.3, 1];

// "THE PERSON BEHIND PHOTO PIXEL®" — premium editorial founder, half-portrait only.
export default function Founder() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-5%", "5%"]);

  return (
    <section ref={ref} id="about" data-testid="founder-section" className="relative overflow-hidden bg-white py-24 md:py-36">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="mb-16 md:mb-20">
          <p className="overline mb-6 text-pp-red">The Founder</p>
          <h2 className="font-display text-5xl font-black leading-[0.92] tracking-tight text-pp-ink sm:text-6xl lg:text-7xl">
            THE PERSON<br />BEHIND PHOTO PIXEL®
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-0">
          {/* Half portrait */}
          <div className="md:col-span-6">
            <div className="relative mx-auto w-full max-w-md md:mx-0">
              <motion.div
                aria-hidden
                className="pointer-events-none absolute -left-4 -top-4 z-0 h-full w-full border-2 border-pp-red md:-left-5 md:-top-5"
                initial={{ clipPath: "inset(0 100% 100% 0)" }}
                whileInView={{ clipPath: "inset(0 0 0 0)" }}
                viewport={{ once: true, margin: "-15% 0px" }}
                transition={{ duration: 1.1, ease: EASE, delay: 0.2 }}
              />
              <motion.div
                className="relative z-10 aspect-[4/5] overflow-hidden bg-neutral-100"
                style={{ y: imgY }}
                initial={{ clipPath: "inset(100% 0 0 0)" }}
                whileInView={{ clipPath: "inset(0% 0 0 0)" }}
                whileHover={{ scale: 1.02 }}
                viewport={{ once: true, margin: "-15% 0px" }}
                transition={{ duration: 1.0, ease: EASE }}
                data-cursor="view"
              >
                <img
                  src={BRAND.founder.photo}
                  alt={BRAND.founder.name}
                  data-testid="founder-photo"
                  className="h-full w-full object-cover"
                  style={{ objectPosition: "50% 8%", transform: "scale(1.3)", transformOrigin: "50% 0%" }}
                />
              </motion.div>
              <div className="absolute -bottom-4 left-6 z-20 bg-pp-red px-4 py-2">
                <span className="overline text-white">PHOTO PIXEL®</span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="relative hidden md:col-span-1 md:flex md:items-stretch md:justify-center">
            <motion.div
              className="w-px bg-pp-red"
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 1, ease: EASE, delay: 0.3 }}
              style={{ transformOrigin: "top" }}
            />
          </div>

          {/* Text */}
          <div className="flex flex-col justify-center md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
            >
              <h3 className="font-display text-4xl font-bold tracking-tight text-pp-ink md:text-5xl">{BRAND.founder.name}</h3>
              <p className="overline mt-4 text-pp-red">{BRAND.founder.role}</p>
              <p className="overline mt-1 text-neutral-500">PHOTO PIXEL®</p>
              <div className="my-8 h-px w-16 bg-neutral-300" />
              <p className="font-display text-2xl italic leading-relaxed text-pp-ink md:text-[1.7rem]">
                “{BRAND.founder.quote}”
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
